/**
 * Frontend mirror of canvas-session types (src/types/canvas-session.ts).
 * Kept in sync manually — the backend is the source of truth.
 */

export type SectionStatus = 'generating' | 'generated' | 'approved' | 'failed';
export type CanvasSessionStatus = 'generating' | 'active' | 'pressing' | 'complete' | 'failed';

export type ClusterId = 'story-core' | 'world' | 'cast' | 'engine' | 'craft' | 'meta';

export interface ProseData { type: 'prose'; prose: string }
export interface EntityData { type: 'entity'; name: string; age?: string; role: string; wound: string; want: string; need: string; arc: string; monogram: string }
export interface ListData { type: 'list'; items: Array<{ name: string; detail: string }> }
export interface CastData { type: 'cast'; characters: Array<{ name: string; role: string; description: string; monogram: string }> }
export interface WebData { type: 'web'; nodes: Array<{ id: string; name: string; role: string }>; edges: Array<{ from: string; to: string; label: string; edgeType: string }> }
export interface ArcData { type: 'arc'; keyMoments: Array<{ position: number; label: string; character?: string }>; description: string }
export interface VoiceData { type: 'voice'; prose: string }
export interface ConstraintsData { type: 'constraints'; constraints: string[] }
export interface BeatsData { type: 'beats'; beats: Array<{ name: string; feeling: string; isClimax?: boolean }> }

export type SectionData = ProseData | EntityData | ListData | CastData | WebData | ArcData | VoiceData | ConstraintsData | BeatsData;

export interface BriefSection {
  id: string;
  clusterId: ClusterId;
  title: string;
  data: SectionData;
  status: SectionStatus;
  approvedAt?: string;
}

export interface Tier1Inputs {
  genre: string;
  emotionalCore: string;
  compTitles: string;
  scene: string;
  toneDarkness: 1 | 2 | 3 | 4 | 5;
}

export interface CascadeWarning {
  sourceSection: string;
  affectedSection: string;
  reason: string;
  createdAt: string;
  dismissed?: boolean;
}

export interface CanvasSession {
  sessionId: string;
  pseudonymSlug: string;
  tier1Inputs: Tier1Inputs;
  sections: BriefSection[];
  approvalCount: number;
  status: CanvasSessionStatus;
  cascadeWarnings?: CascadeWarning[];
  createdAt: string;
  updatedAt: string;
}

export const SECTION_MANIFEST: Array<{ id: string; clusterId: ClusterId; title: string; fullWidth?: boolean }> = [
  { id: 'premise', clusterId: 'story-core', title: 'Premise', fullWidth: true },
  { id: 'protagonist', clusterId: 'story-core', title: 'Protagonist' },
  { id: 'antagonist', clusterId: 'story-core', title: 'Antagonist' },
  { id: 'setting', clusterId: 'world', title: 'Setting' },
  { id: 'key-places', clusterId: 'world', title: 'Key Places' },
  { id: 'important-objects', clusterId: 'world', title: 'Important Objects' },
  { id: 'supporting-cast', clusterId: 'cast', title: 'Supporting Cast' },
  { id: 'connection-web', clusterId: 'cast', title: 'Connection Web', fullWidth: true },
  { id: 'stakes', clusterId: 'engine', title: 'Stakes' },
  { id: 'tension-arc', clusterId: 'engine', title: 'Tension Arc', fullWidth: true },
  { id: 'voice', clusterId: 'craft', title: 'Voice', fullWidth: true },
  { id: 'theme', clusterId: 'craft', title: 'Theme' },
  { id: 'reader-expectations', clusterId: 'craft', title: 'Reader Expectations' },
  { id: 'emotional-beats', clusterId: 'craft', title: 'Emotional Beats' },
  { id: 'comp-titles', clusterId: 'meta', title: 'Comp Titles' },
  { id: 'constraints', clusterId: 'meta', title: 'Constraints' },
];
